import { Box, Field, SimpleGrid, Text } from '@chakra-ui/react';
import {
  CollabType,
  UGC_BRIEF_MAX_END_DAYS,
  compensationOfferedProduct,
  type BriefTemplateContent,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { QuantityStepper } from '@/pages/collabs/brief-templates/editor/controls/QuantityStepper';
import {
  DEFAULT_END_DAYS,
  DEFAULT_FIRST_CONTENT_DAYS,
} from '@/pages/collabs/brief-templates/_shared/brief-content';

interface CollabTermsScheduleProps {
  /**
   * A UGC counts from the product reception, or from the funding when it ships
   * no product, and ends within its ceiling.
   */
  ugc?: boolean;
  content: BriefTemplateContent;
  updateContent: (updater: (prev: BriefTemplateContent) => BriefTemplateContent) => void;
  respondByDays: number;
  onRespondByDaysChange: (days: number) => void;
}

interface ScheduleCardProps {
  label: string;
  hint: string;
  value: number;
  onChange: (value: number) => void;
  daysFormat: (value: number) => string;
  max?: number;
}

const ScheduleCard = ({ label, hint, value, onChange, daysFormat, max }: ScheduleCardProps) => (
  <Box borderWidth="1px" borderColor="cardBorder" rounded="lg" bg="panelBg" px="12px" py="2.5">
    <Text
      fontFamily="heading"
      fontSize="10.5px"
      fontWeight="semibold"
      textTransform="uppercase"
      letterSpacing="0.05em"
      color="fg.muted"
      mb="1.5"
    >
      {label}
    </Text>
    <QuantityStepper
      variableStyle
      value={value}
      onChange={onChange}
      max={max}
      format={daysFormat}
      valueMinW="52px"
      aria-label={label}
    />
    <Text fontSize="10.5px" color="fg.muted" mt="1.5">
      {hint}
    </Text>
  </Box>
);

/**
 * The three per-recipient deadlines of the wizard terms step: expected response
 * (proposal expiry, wizard-only), first content, and collaboration end (both
 * relative to acceptance, or for a UGC to the product reception, else to the
 * funding when it ships no product, whose end
 * stops at the UGC ceiling). The terms step seeds the design defaults into the
 * content before rendering, so values here are always concrete.
 */
export const CollabTermsSchedule = ({
  ugc = false,
  content,
  updateContent,
  respondByDays,
  onRespondByDaysChange,
}: CollabTermsScheduleProps) => {
  const { t } = useTranslation();
  const { timeline } = content;
  const afterHint = t(
    !ugc
      ? 'invitations.modal.collab.wizard.terms.afterAcceptanceHint'
      : compensationOfferedProduct(content.compensation, CollabType.UGC)
        ? 'invitations.modal.collab.wizard.terms.ugcAfterReceptionHint'
        : 'invitations.modal.collab.wizard.terms.ugcAfterFundingHint'
  );

  const daysFormat = (v: number) =>
    t('collabs.briefTemplates.sections.timeline.days', { count: v });

  return (
    <Field.Root>
      <Field.Label fontFamily="heading" fontSize="12.5px" fontWeight="semibold">
        {t('invitations.modal.collab.wizard.terms.scheduleLabel')}
      </Field.Label>
      <SimpleGrid columns={{ base: 1, sm: 3 }} gap="2.5" w="full">
        <ScheduleCard
          label={t('invitations.modal.collab.wizard.terms.respondByLabel')}
          hint={t('invitations.modal.collab.wizard.terms.respondByHint')}
          value={respondByDays}
          onChange={onRespondByDaysChange}
          daysFormat={daysFormat}
        />
        <ScheduleCard
          label={t('invitations.modal.collab.wizard.terms.firstContentLabel')}
          hint={afterHint}
          value={timeline.firstContentDays ?? DEFAULT_FIRST_CONTENT_DAYS}
          onChange={(value) =>
            updateContent((prev) => ({
              ...prev,
              timeline: { ...prev.timeline, firstContentDays: value },
            }))
          }
          daysFormat={daysFormat}
        />
        <ScheduleCard
          label={t('invitations.modal.collab.wizard.terms.endLabel')}
          hint={afterHint}
          value={timeline.endDays ?? DEFAULT_END_DAYS}
          max={ugc ? UGC_BRIEF_MAX_END_DAYS : undefined}
          onChange={(value) =>
            updateContent((prev) => ({
              ...prev,
              timeline: { ...prev.timeline, endDays: value },
            }))
          }
          daysFormat={daysFormat}
        />
      </SimpleGrid>
    </Field.Root>
  );
};
